// AdminDashboardMain.jsx

import React, { useEffect } from "react";
import { AiOutlineMoneyCollect, AiOutlineArrowRight } from "react-icons/ai";
import { MdBorderClear } from "react-icons/md";
import { Link } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfAdmin } from "../../redux/actions/order";
import { getAllSellers } from "../../redux/actions/sellers";
import Loader from "../Layout/Loader";
import axios from "axios";
import { server } from "../../server";

const AdminDashboardMain = () => {
  const dispatch = useDispatch();

  const { adminOrders = [], adminOrderLoading } = useSelector((state) => state.order || {});
  const { sellers = [] } = useSelector((state) => state.seller || {});

  // Only keep adminSales and loadingInvoices
  const [adminSales, setAdminSales] = React.useState(0);
  const [loadingInvoices, setLoadingInvoices] = React.useState(true);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);

  // Add state for date range
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");

  // Update fetchInvoices to use date range
  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([
          dispatch(getAllOrdersOfAdmin()),
          dispatch(getAllSellers())
        ]);
      } finally {
        setIsInitialLoading(false);
      }
    };
    
    fetchData();
    
    // Fetch all invoices for admin sales
    const fetchInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const params = startDate && endDate ? { startDate, endDate } : {};
        const response = await axios.get(`${server}/invoice/get-all-invoices`, {
          params,
          withCredentials: true,
        });
        // Calculate adminSales as sum of totalInvoiceValue
        const total = response.data.reduce((acc, inv) => acc + Number(inv.totalInvoiceValue || 0), 0);
        setAdminSales(total);
      } catch (err) {
        setAdminSales(0);
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [dispatch, startDate, endDate]);

  const adminBalance = adminSales.toFixed(2);

  const columns = [
    { field: "id", headerName: "Order ID", minWidth: 150, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 1,
      cellClassName: (params) => {
        return params.row.status === "Delivered" ? "text-green-600 font-semibold" : "text-gray-600 font-semibold";
      },
    },
    {
      field: "itemsQty",
      headerName: "Items Qty",
      type: "number",
      minWidth: 130,
      flex: 1,
    },
    {
      field: "total",
      headerName: "Total",
      type: "number",
      minWidth: 130,
      flex: 1,
      renderCell: (params) => `Rs ${params.value}`,
    },
    {
      field: "createdAt",
      headerName: "Order Date",
      minWidth: 130,
      flex: 1,
      renderCell: (params) => {
        // Check if createdAt exists and is valid before rendering
        if (!params.row.createdAt) return "N/A";
        const date = new Date(params.row.createdAt);
        return isNaN(date) ? "N/A" : date.toLocaleDateString();
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        return (
          <Link to={`/admin/order/${params.id}`}>
            <Button variant="contained" color="primary" className="flex items-center justify-center">
              <AiOutlineArrowRight size={20} />
            </Button>
          </Link>
        );
      },
    },
  ];

  const rows = adminOrders.map((order) => ({
    id: order._id || "N/A",
    itemsQty: order.cart
      ? order.cart.reduce((acc, cartItem) => acc + cartItem.qty, 0)
      : 0,
    total: order.shopTotalOrderPriceWithCommission
      ? order.shopTotalOrderPriceWithCommission.toFixed(2)
      : "0.00",
    status: order.status || "N/A",
    createdAt: order.createdAt || null,
  }));

  return (
    <>
      {isInitialLoading || adminOrderLoading ? (
        <Loader />
      ) : (
        <div className="w-full p-4 sm:p-8 bg-gray-50 min-h-screen">
          {/* Overview Section */}
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">Overview</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            {/* Total Sales Card */}
            <div className="flex flex-col bg-white shadow-md rounded-lg p-6">
              <div className="flex items-center mb-4">
                <AiOutlineMoneyCollect size={30} className="text-green-500 mr-3" />
                <h3 className="text-lg font-medium text-gray-700">Total Sales</h3>
              </div>
              {/* Date Range Inputs */}
              <div className="flex gap-2 mb-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                  max={endDate || undefined}
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                  min={startDate || undefined}
                />
                {(startDate || endDate) && (
                  <button
                    className="ml-2 text-xs text-blue-600 underline"
                    onClick={() => { setStartDate(""); setEndDate(""); }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <h5 className="text-2xl font-bold text-gray-800 mb-2">
                {loadingInvoices ? <span>Loading...</span> : <>Rs {adminBalance}</>}
              </h5>
            </div>

            {/* All Sellers Card */}
            <div className="flex flex-col bg-white shadow-md rounded-lg p-6">
              <div className="flex items-center mb-4">
                <MdBorderClear size={30} className="text-blue-500 mr-3" />
                <h3 className="text-lg font-medium text-gray-700">All Sellers</h3>
              </div>
              <h5 className="text-2xl font-bold text-gray-800 mb-2">{sellers.length}</h5>
              <Link to="/admin-sellers" className="text-blue-600 hover:underline mt-auto">
                View Sellers
              </Link>
            </div>

            {/* All Orders Card */}
            <div className="flex flex-col bg-white shadow-md rounded-lg p-6">
              <div className="flex items-center mb-4">
                <AiOutlineMoneyCollect size={30} className="text-purple-500 mr-3" />
                <h3 className="text-lg font-medium text-gray-700">All Orders</h3>
              </div>
              <h5 className="text-2xl font-bold text-gray-800 mb-2">{adminOrders.length}</h5>
              <Link to="/admin-orders" className="text-blue-600 hover:underline mt-auto">
                View Orders
              </Link>
            </div>
          </div>

          {/* Latest Orders Section */}
          <div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Latest Orders</h3>
            <div className="bg-white shadow-md rounded-lg p-4 overflow-auto">
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={5}
                disableSelectionOnClick
                autoHeight
                className="bg-white"
                getRowClassName={(params) =>
                  params.row.status === "Delivered" ? "bg-green-50" : "bg-white"
                }
                components={{
                  NoRowsOverlay: () => (
                    <div className="flex justify-center items-center h-full">
                      <span className="text-gray-500">No orders available.</span>
                    </div>
                  ),
                }}
                // Responsive adjustments using sx
                sx={{
                  "& .MuiDataGrid-cell": {
                    padding: "8px",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f3f4f6",
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                  "& .MuiButton-root": {
                    padding: "6px 12px",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboardMain;


